import {Grid, InputAdornment} from '@material-ui/core';
import Drawer from '@material-ui/core/Drawer';
import {GridRowParams} from '@material-ui/data-grid';
import {SearchSharp} from '@material-ui/icons';
import {AxiosResponse} from 'axios';
import {ReactElement, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDebouncedCallback} from 'use-debounce';
import {MerchantContext} from '../../App';
import {INSTITUTIONS_URL, PAYMENTS_SUMMARY_URL, PAYMENTS_URL} from '../../shared/constants/api-urls';
import {DEFAULT_TABLE_PAGE_SIZE} from '../../shared/constants/app-constants';
import useGet, {useGetRaw} from '../../shared/hooks/use-get.hook';
import {EnvironmentType} from '../../shared/model/EnvironmentType.model';
import {PageInfo} from '../../shared/model/Page.model';
import {PaymentStatus} from '../../shared/model/PaymentStatus.model';
import {currencyFormat} from '../../shared/utils/currencyFormat';
import {filterFactory} from '../../shared/utils/filterFactory';
import {TransHelper} from '../../utils/trans-helper';
import {PaymentLinkRequestParams} from '../pay-by-link/PayByLink.model';
import EnvSwitch from '../shared/envSwitch/EnvSwitch';
import Filters, {FilterParams} from '../shared/filterTable/Filters';
import MainContainer from '../shared/main-container/MainContainer';
import NxButton from '../shared/nxButton/NxButton';
import NxInput from '../shared/nxInput/NxInput';
import NxLoader, {NxLoaderVariant} from '../shared/nxLoader/NxLoader';
import {NxSelectOption} from '../shared/nxSelect/NxSelect';
import DataTable from '../shared/table/Table';
import {
  fromPaymentTo,
  Payment,
  PaymentRequestParams,
  PaymentsResponse,
  PaymentStatistics,
  PaymentTO
} from './Payment.model';
import PaymentCard from './paymentCard/PaymentCard';
import PaymentDetails from './paymentDetails/PaymentDetails';
import styles from './Payments.module.scss';
import {paymentsTableColumnDefinitions} from './paymentsTableColumnDefinitions';
import {SettlementInstitution} from '../../shared/model/SettlementInstitution.model';
import dayjs from 'dayjs';
import {DEFAULT_DATE_PICKER_VALUE_FORMAT} from '../shared/nxDatePicker/NxDatePicker';

export const PrefixTrans = TransHelper.getPrefixedTrans('PAYMENTS');
const SharedTrans = TransHelper.getPrefixedTrans('SHARED');

const getDefaultRequestParam = (currentMerchantId: number, env: EnvironmentType): PaymentLinkRequestParams => (
  {
    env,
    pageSize: DEFAULT_TABLE_PAGE_SIZE,
    merchantId: currentMerchantId,
    dateFrom: dayjs().format(DEFAULT_DATE_PICKER_VALUE_FORMAT),
    dateTo: dayjs().format(DEFAULT_DATE_PICKER_VALUE_FORMAT),
    status: PaymentStatus.EXECUTED
  }
);

export default function Payments(): ReactElement {

  const {t} = useTranslation();
  const [institutions, setInstitutions] = useState<SettlementInstitution[]>();
  const [payments, setPayments] = useState<Array<PaymentTO>>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo>();
  const [paymentStatistics, setPaymentStatistics] = useState<PaymentStatistics>();
  const [search, setSearch] = useState<string>('');
  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingStats, setLoadingStats] = useState<boolean>(true);
  const [envError, setEnvError] = useState<boolean>(false);
  const [downloadInProgress, setDownloadInProgress] = useState<boolean>(false);
  const {currentMerchantId, env, setEnv} = useContext(MerchantContext);
  const [paymentRequestParams, setPaymentRequestParams] = useState<PaymentRequestParams | undefined>(
    currentMerchantId ? getDefaultRequestParam(currentMerchantId, env) : undefined
  );
  const getPayments = useGet<PaymentsResponse>(`${PAYMENTS_URL}`);
  const getPaymentsSummary = useGet<PaymentStatistics>(PAYMENTS_SUMMARY_URL);
  const getInstitutions = useGet<SettlementInstitution[]>(INSTITUTIONS_URL);
  const paymentsCsvRequest = useGetRaw<string>(`${PAYMENTS_URL}/csv`);

  const updateData = useCallback(async () => {
    if (!paymentRequestParams) {
      return;
    }

    setLoadingStats(true);
    setLoading(true);
    setEnvError(false);

    // fetch summary stats
    await getPaymentsSummary(filterFactory(paymentRequestParams))
      .then(setPaymentStatistics)
      .catch(() => setPaymentStatistics(undefined));
    setLoadingStats(false);

    // fetch table data
    await getPayments(filterFactory(paymentRequestParams))
      .then((response: PaymentsResponse) => {
        setPageInfo(response);
        setPayments(response.result);
      })
      .catch((): void => {
        setPageInfo({
          pageNo: 0,
          resultCount: 0,
          totalCount: 0
        });
        setPayments([]);
        setEnvError(true);
      });
    setLoading(false);
  }, [paymentRequestParams]);

  const paymentsWithInstitutionName: Payment[] = useMemo(() => {
    return payments.map((payment: PaymentTO) => fromPaymentTo({
      ...payment,
      institutionName: institutions?.find((institution) =>
        institution.code === payment.institutionCode)?.name || ''
    }));
  }, [payments, institutions]);

  useEffect(() => {
    getInstitutions(new URLSearchParams({env}))
      .then(setInstitutions);
  }, [env]);

  useEffect(() => {
    if (institutions) {
      updateData();
    }
  }, [paymentRequestParams, institutions]);

  useEffect(() => {
    if (currentMerchantId) {
      setPaymentRequestParams((previousReqParams?: PaymentRequestParams) =>
        previousReqParams
          ? ({
            ...previousReqParams,
            merchantId: currentMerchantId,
            env
          })
          : getDefaultRequestParam(currentMerchantId, env)
      );
    }
  }, [currentMerchantId]);

  const handleFilterChange = (filterParams: FilterParams): void => {
    if (currentMerchantId) {
      setPaymentRequestParams((previousReqParams?: PaymentRequestParams) => ({
        ...previousReqParams,
        ...filterParams,
        ...(filterParams.status && {status: PaymentStatus[filterParams.status]}),
        pageNo: 0,
        merchantId: currentMerchantId,
        env
      }));
    }
  };

  const handlePageChange = (pageNo: number): void => {
    if (currentMerchantId) {
      setPaymentRequestParams((previousReqParams?: PaymentRequestParams) => ({
        ...previousReqParams,
        pageNo: pageNo,
        merchantId: currentMerchantId,
        env
      }));
    }
  };

  const handleDownloadCsv = async (): Promise<void> => {
    if (!paymentRequestParams) {
      return;
    }
    setDownloadInProgress(true);
    await paymentsCsvRequest(filterFactory(paymentRequestParams))
      .then((response: AxiosResponse<string>) => {
        // As filename is included in header:
        // content-disposition: attachment; filename=swiftpay-payments-1650022658434.csv
        const contentDispositionHeaderValue: string = response.headers['content-disposition'];
        const fileName = contentDispositionHeaderValue.split('filename=')[1];
        const data = new Blob([response.data], {type: 'text/csv'});
        const csvURL = window.URL.createObjectURL(data);
        const tempLink = document.createElement('a');
        tempLink.href = csvURL;
        tempLink.setAttribute('download', fileName);
        tempLink.click();
      });
    setDownloadInProgress(false);
  };

  const toggleDetails = (open: boolean, params?): void => {
    if (params) {
      setDetailsId(params.id.toString());
    } else {
      setDetailsId(null);
    }
    setOpenDetails(open);
  };

  const debouncedSearchInput = useDebouncedCallback((value) => {
    if (currentMerchantId) {
      setPaymentRequestParams((previousReqParams?: PaymentRequestParams) => ({
        ...previousReqParams,
        phrase: value,
        merchantId: currentMerchantId,
        env
      }));
    }
  }, 2000);

  const handleSearchInput = (e): void => {
    setSearch(e.target.value);
    debouncedSearchInput(e.target.value);
  };

  const handleEnvChange = (environment: EnvironmentType): void => {
    setEnv(environment);
    if (currentMerchantId) {
      setPaymentRequestParams((previousReqParams?: PaymentRequestParams) => ({
        ...previousReqParams,
        merchantId: currentMerchantId,
        env: environment
      }));
    }
  };

  const getStatusOptions = (): NxSelectOption[] => Object.keys(PaymentStatus)
    .map((key) => ({
      label: t(`SHARED.PAYMENT.STATUS.${PaymentStatus[key]}`),
      value: PaymentStatus[key]
    } as NxSelectOption));

  const formatPaymentStatisticValue = (value?: number): string => value ? currencyFormat(value) : 'N/A';

  return (
    <>
      <Drawer anchor={'right'} open={openDetails}>
        {
          detailsId?.length
            ? <PaymentDetails
              id={detailsId}
              env={env}
              institutions={institutions}
              onClose={(): void => toggleDetails(false)} />
            : <div>Error</div>
        }
      </Drawer>
      <MainContainer>
        <Grid className={styles.pageHeader} container direction='row' justifyContent={'space-between'}>
          <div className={styles.title}>
            <PrefixTrans>TITLE</PrefixTrans>
          </div>
          <EnvSwitch onChange={handleEnvChange} value={env} />
        </Grid>
        <Grid className={styles.filtersContainer} item xs={12}>
          <Filters
            onChange={handleFilterChange}
            selectOptions={getStatusOptions()}
            disableFutureDates />
        </Grid>
        <Grid container spacing={4}>
          <Grid item xs={4}>
            <PaymentCard
              title={t('PAYMENTS.SUMMARY.AVG_AMOUNT')}
              count={formatPaymentStatisticValue(paymentStatistics?.averageAmount)}
              loading={loadingStats} />
          </Grid>
          <Grid item xs={4}>
            <PaymentCard
              title={t('PAYMENTS.SUMMARY.TOTAL_AMOUNT')}
              count={formatPaymentStatisticValue(paymentStatistics?.totalAmount)}
              loading={loadingStats} />
          </Grid>
          <Grid item xs={4}>
            <PaymentCard
              title={t('PAYMENTS.SUMMARY.TOTAL_COUNT')}
              count={paymentStatistics?.totalCount ?? 'N/A'}
              loading={loadingStats} />
          </Grid>
        </Grid>
        <Grid
          className={styles.tableHeader}
          container
          direction='row'
          justifyContent='space-between'
          alignItems='center'>
          <div className={styles.tableTitle}>
            <PrefixTrans>TABLE.HEADER_TITLE</PrefixTrans>
          </div>
          <div className={styles.downloadSearchContainer}>
            <NxButton
              className={styles.download}
              onClick={handleDownloadCsv}
              disabled={!pageInfo || pageInfo.totalCount === 0 || downloadInProgress}>
              <NxLoader variant={NxLoaderVariant.SMALL} loaded={!downloadInProgress}>
                <SharedTrans>DOWNLOAD</SharedTrans>
              </NxLoader>
            </NxButton>
            <NxInput
              type='search'
              label={''}
              placeholder={'Search...'}
              value={search}
              className={styles.searchInput}
              onChange={handleSearchInput}
              startAdornment={
                <InputAdornment position='end'>
                  <SearchSharp />
                </InputAdornment>
              } />
          </div>
        </Grid>
        <Grid className={styles.tableContainer}>
          <DataTable
            loading={loading}
            pageInfo={pageInfo}
            rows={paymentsWithInstitutionName}
            onRowClick={(param: GridRowParams): void => toggleDetails(true, param)}
            onPageChange={handlePageChange}
            pageSize={paymentRequestParams?.pageSize}
            columns={paymentsTableColumnDefinitions(t)}
            noRowsMessage={
              envError
                ? t('SHARED.NO_DATA_ENV', {environmentType: env})
                : t('SHARED.NO_DATA')
            } />
        </Grid>
      </MainContainer>
    </>
  );
}
